import { fetchContentful } from './contentfulClient';
import { getCache, setCache } from './cacheUtils';

export const CACHE_TTL = {
  THIRTY_DAYS: 30 * 24 * 60 * 60 * 1000,
  SIX_HOURS: 6 * 60 * 60 * 1000,
  ONE_HOUR: 60 * 60 * 1000,
};

const queryAnnouncement = `
{
  announcementCollection { items { text } }
  websiteConfigurationCollection { items { announcementEnabled } }
}
`;

export const fetchAnnouncement = async () => {
  const data = await fetchContentful(queryAnnouncement);
  return {
    data: data.announcementCollection.items,
    options: data.websiteConfigurationCollection.items[0],
  };
};

const queryWorkshopItems = `
{
  workshopCollection {
    items { title description price category }
  }
}
`;

export const fetchWorkshopItems = async () => {
  const data = await fetchContentful(queryWorkshopItems);
  return data.workshopCollection.items;
};

const queryWorkshopPricelist = `
{
  workshopPricelistCollection {
    items {
      title
      order
      pricelistItemsCollection {
        items {
          ... on Workshop {
            sys { id }
            title
            price
            description
            category
          }
        }
      }
    }
  }
}
`;

export const fetchWorkshopPricelist = async () => {
  const data = await fetchContentful(queryWorkshopPricelist);
  return data.workshopPricelistCollection.items.sort(
    (a, b) => a.order - b.order,
  );
};

const ARTICLES_CACHE_KEY = 'articlesCache';

const queryArticles = `
{
  articlesCollection {
    items {
      sys { id }
      title
      category
      text { json }
      coverImage {
        url(transform: { width: 400, format: WEBP, quality: 70 })
      }
    }
  }
}
`;

export const fetchAllArticles = async () => {
  const cached = getCache(ARTICLES_CACHE_KEY, CACHE_TTL.ONE_HOUR);
  if (cached) return cached;

  const data = await fetchContentful(queryArticles);
  const articles = data.articlesCollection.items;
  setCache(ARTICLES_CACHE_KEY, articles);
  return articles;
};

const ARTICLE_CACHE_PREFIX = 'articleCache_';

const queryArticleById = `
query($id: String!) {
  articlesCollection(where: { sys: { id: $id } }, limit: 1) {
    items {
      sys { id publishedAt }
      title
      category
      text { json }
      coverImage {
        url(transform: { width: 1000, format: WEBP, quality: 50 })
      }
    }
  }
}
`;

export const fetchArticleById = async (id) => {
  const cacheKey = `${ARTICLE_CACHE_PREFIX}${id}`;
  const cached = getCache(cacheKey, CACHE_TTL.THIRTY_DAYS);
  if (cached) return cached;

  const data = await fetchContentful(queryArticleById, { id });
  const article = data.articlesCollection.items[0];
  setCache(cacheKey, article);
  return article;
};

const WORK_ARTICLES_KEY = 'workArticlesCache';

const queryWorkArticles = `
{
  articlesCollection(where: { category: "work" }) {
    items {
      sys { id }
      title
      category
      coverImage {
        url(transform: { width: 400, format: WEBP, quality: 50 })
      }
    }
  }
}
`;

export const fetchWorkArticles = async () => {
  const cached = getCache(WORK_ARTICLES_KEY, CACHE_TTL.ONE_HOUR);
  if (cached) return cached;

  const data = await fetchContentful(queryWorkArticles);
  const workArticles = data.articlesCollection.items;
  setCache(WORK_ARTICLES_KEY, workArticles);
  return workArticles;
};

const queryStoreInfo = `
{
  storeInfoCollection {
    items {
      address
      postcode
      email
      phone
      region
      openHoursCollection {
        items {
          ... on StoreOpenHours {
            daysInterval
            timeInterval
            exceptional
          }
        }
      }
    }
  }
}
`;

export const fetchStoreInfo = async () => {
  const data = await fetchContentful(queryStoreInfo);
  const { openHoursCollection, ...rest } = data.storeInfoCollection.items[0];

  const openHoursGrouped = {
    exceptional: [],
    normal: [],
  };

  openHoursCollection.items.forEach((item) => {
    (item.exceptional
      ? openHoursGrouped.exceptional
      : openHoursGrouped.normal
    ).push(item);
  });

  return { ...rest, openhours: openHoursGrouped };
};
